import {Form, Steps} from 'antd';
import React, {forwardRef, useImperativeHandle} from 'react';

import {Fields} from 'components/form/fields/fields';
import {ModalOpenerComponent} from 'components/modal-opener-component';
import {ITableActionProps} from 'components/table/action/table-action-types';
import {TEntityValue} from 'modules/data';

import {CommentsFormContent} from './components';
import {stepIndex, EStepTitle} from './document-status-change-modal.constants';
import {IDocumentStatusChangeForm} from './document-status-change-modal.types';
import {getModalTitle, getSaveButtonLabel} from './document-status-change-modal.utils';
import {useDocumentStatusChangeModal} from './hooks/use-document-status-change-modal';
import './document-status-change-modal.less';

interface IDocumentStatusChangeModalProps extends ITableActionProps {
    selectedDocuments: Record<string, TEntityValue>[];
}

export interface IDocumentStatusChangeModalRef {
    openModal: () => void;
}

export const DocumentStatusChangeModal = forwardRef<IDocumentStatusChangeModalRef, IDocumentStatusChangeModalProps>(({
    meta,
    entityName,
    selectedDocuments,
}: IDocumentStatusChangeModalProps, ref) => {
    const {
        modalRef,
        isOpen,
        form,
        firstSelectedEntity,
        currentStep,
        isStepsEnabled,
        isCommentFieldEnabled,
        finalStatus,
        publishFormMetadata,
        setIsOpen,
        handleCloseModal,
        handleSubmit,
        handleOpenModal,
        handleSave,
    } = useDocumentStatusChangeModal({
        entityName, meta, selectedDocuments,
    });

    useImperativeHandle(ref, () => ({
        openModal: () => handleOpenModal(),
    }));

    const modalTitle = getModalTitle(selectedDocuments);
    const saveButtonLabel = getSaveButtonLabel(currentStep);

    return (
        <ModalOpenerComponent
            ref={modalRef}
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            handleSave={handleSave}
            handleCancel={handleCloseModal}
            controlLabels={{
                save: saveButtonLabel,
                cancel: 'Отменить',
            }}
            modalProps={{
                title: modalTitle,
                forceRender: false,
                destroyOnClose: true,
                centered: true,
            }}
        >
            <Form<IDocumentStatusChangeForm>
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
            >
                {isStepsEnabled && (isCommentFieldEnabled ? (
                    <>
                        <Steps
                            className="document-status-change-modal__steps"
                            current={currentStep}
                            size="small"
                        >
                            <Steps.Step title={EStepTitle.COMMENTS} />
                            <Steps.Step title={EStepTitle.PUBLISH} />
                        </Steps>
                        {(firstSelectedEntity?.docStatusMeaning && finalStatus) && (
                            <div className="status-transition">
                                <div className="status-transition__text">
                                    {firstSelectedEntity?.docStatusMeaning} → {finalStatus}
                                </div>
                            </div>
                        )}
                    </>
                ) : (
                    <span className="document-status-change-modal__step-title">{EStepTitle.PUBLISH}</span>
                ))}
                {isCommentFieldEnabled && (
                    <CommentsFormContent
                        selectedDocuments={selectedDocuments}
                        form={form}
                        hidden={currentStep !== stepIndex[EStepTitle.COMMENTS]}
                    />
                )}
                {currentStep === stepIndex[EStepTitle.PUBLISH] && (
                    <div className="publish-form-content">
                        <Fields
                            list={publishFormMetadata?.fields ?? []}
                            form={form}
                            entityName={entityName}
                        />
                    </div>
                )}
            </Form>
        </ModalOpenerComponent>
    );
});
